import React, { useState } from 'react';
import {
  Box,
  Card,
  CardContent,
  Tooltip,
  Typography,
  IconButton,
  Collapse,
} from '@mui/material';
import Logo from '../Icons/Logo';
import { AvailableModule } from '../../types/types';
import { Visibility, VisibilityOff } from '@mui/icons-material';
import { useBrandLanguage } from '@context/brand-language/context';
import { Avatar } from '@mui/material';
import { useUserPicture } from '@features/user';
import { NoteSectionDisplayBox } from '../Note/NoteSectionDisplayBox';

interface NewModuleBlockProps {
  module: AvailableModule;
  isSelected?: boolean;
  onToggleSelect?: (moduleId: string) => void;
  onPreviewToggle?: (moduleId: string, action: 'show' | 'hide') => void;
}

const NewModuleBlock = ({
  module,
  isSelected,
  onToggleSelect,
  onPreviewToggle,
}: NewModuleBlockProps) => {
  const [showPreview, setShowPreview] = useState(false);
  const { brandLanguage } = useBrandLanguage();
  const userPicture = useUserPicture();

  return (
    <Card
      onClick={() => onToggleSelect?.(module.module_id)}
      sx={{
        my: 1.5,
        border: '1px solid',
        borderColor: isSelected ? 'primary.main' : 'divider',
        borderRadius: 2,
        bgcolor: isSelected ? 'rgba(100, 66, 214, 0.08)' : 'background.paper',
        transition: 'all 0.2s ease-in-out',
        boxShadow: isSelected ? 1 : 0,
        cursor: 'pointer',
        '&:hover': {
          boxShadow: 2,
          borderColor: isSelected ? 'primary.main' : 'action.hover',
        },
      }}
    >
      <CardContent sx={{ py: 1, '&.MuiCardContent-root:last-child': { pb: 1 } }}>
        <Box
          sx={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'flex-start',
            gap: 2,
          }}
        >
          {/* Module info */}
          <Box
            id="module-text"
            sx={{
              display: 'flex',
              flexDirection: 'column',
              justifyContent: 'flex-start',
              flexGrow: 1,
              gap: 1,
            }}
          >
            <Typography sx={{ fontStyle: "normal", fontWeight: "700" }}>
              {module.name}
            </Typography>
            <Typography sx={{fontSize: ".8rem", fontWeight: "300",  color: 'text.secondary' }}>
              {module.description}
            </Typography>
          </Box>

          {/* RIGHT: Actions */}
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, flexShrink: 0 }}>
            <Tooltip title={showPreview ? 'Hide preview' : 'Show preview'} placement="top" arrow>
              <IconButton
                onClick={e => {
                  e.stopPropagation();
                  const newShowPreview = !showPreview;
                  setShowPreview(newShowPreview);
                  onPreviewToggle?.(module.module_id, newShowPreview ? 'show' : 'hide');
                }}
                size="small"
              >
                {showPreview ? <VisibilityOff fontSize="small" /> : <Visibility fontSize="small" />}
              </IconButton>
            </Tooltip>
            {module.is_custom_user_module ? (
              <Tooltip title="Your customized section" placement="top" arrow>
                <Avatar
                  variant="circular"
                  src={userPicture}
                  alt="user photo"
                  sx={{ width: 24, height: 24 }}
                />
              </Tooltip>
            ) : (
              <Tooltip
                title={`${brandLanguage.brandName} section`}
                placement="top"
                arrow
              >
                <Box>
                  <Logo size={20} />
                </Box>
              </Tooltip>
            )}
          </Box>
        </Box>
        <Collapse in={showPreview} sx={{ mt: 2 }}>
          <NoteSectionDisplayBox>{module?.example || 'No example provided.'}</NoteSectionDisplayBox>
        </Collapse>
      </CardContent>
    </Card>
  );
};

export default NewModuleBlock;
